import React, { useState } from "react";
import {
  CurrencyDollar,
  TrendUp,
  Plus,
  Minus,
  Users,
} from "@phosphor-icons/react";
import { formatCurrency } from "../utils/calculations";
import { IncomeSource, Partner } from "../types";

interface IncomeCardProps {
  monthlyIncome: number;
  incomeSources: IncomeSource[];
  recentIncomeTransactions: any[];
  partners?: Partner[];
}

const IncomeCard: React.FC<IncomeCardProps> = ({
  monthlyIncome,
  incomeSources,
  recentIncomeTransactions,
  partners = [],
}) => {
  const [showDetails, setShowDetails] = useState(false);

  const activeSources = incomeSources.filter((is) => is.isActive);
  const inactiveSources = incomeSources.filter((is) => !is.isActive);

  const getIncomeBreakdown = () => {
    const breakdown: Record<string, number> = {};

    activeSources.forEach((source) => {
      let monthlyAmount: number;
      switch (source.frequency) {
        case "monthly":
          monthlyAmount = source.amount;
          break;
        case "weekly":
          monthlyAmount = source.amount * 4.33;
          break;
        case "biweekly":
          monthlyAmount = source.amount * 2.17;
          break;
        case "yearly":
          monthlyAmount = source.amount / 12;
          break;
        default:
          monthlyAmount = source.amount;
      }

      if (!breakdown[source.category]) {
        breakdown[source.category] = 0;
      }
      breakdown[source.category] += monthlyAmount;
    });

    return breakdown;
  };

  const incomeBreakdown = getIncomeBreakdown();
  const totalFromSources = activeSources.reduce((sum, source) => {
    let monthlyAmount: number;
    switch (source.frequency) {
      case "monthly":
        monthlyAmount = source.amount;
        break;
      case "weekly":
        monthlyAmount = source.amount * 4.33;
        break;
      case "biweekly":
        monthlyAmount = source.amount * 2.17;
        break;
      case "yearly":
        monthlyAmount = source.amount / 12;
        break;
      default:
        monthlyAmount = source.amount;
    }
    return sum + monthlyAmount;
  }, 0);

  const oneTimeIncome = monthlyIncome - totalFromSources;

  const getIncomeColor = (income: number) => {
    if (income >= 10000) return "text-green-500";
    if (income >= 5000) return "text-blue-500";
    if (income >= 2000) return "text-yellow-500";
    return "text-orange-500";
  };

  const getIncomeMessage = (income: number) => {
    if (income >= 10000) return "High income level";
    if (income >= 5000) return "Good income level";
    if (income >= 2000) return "Moderate income level";
    return "Low income level";
  };

  const getFrequencyLabel = (frequency: string) => {
    switch (frequency) {
      case "monthly":
        return "Monthly";
      case "weekly":
        return "Weekly";
      case "biweekly":
        return "Bi-weekly";
      case "yearly":
        return "Yearly";
      default:
        return "Monthly";
    }
  };

  return (
    <div className="card">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-2 sm:space-y-0">
        <div className="flex items-center space-x-2">
          <CurrencyDollar className="w-6 h-6 text-green-500" weight="duotone" />
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Monthly Income
          </h3>
        </div>
        <button
          onClick={() => setShowDetails(!showDetails)}
          className={`text-xs px-3 py-2 rounded transition-colors w-full sm:w-auto ${
            showDetails
              ? "bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50 text-green-700 dark:text-green-300 border border-green-300 dark:border-green-600/50"
              : "bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
          }`}>
          {showDetails ? "Hide Details" : "Show Details"}
        </button>
      </div>

      <div className="space-y-3">
        <div>
          <p className={`text-2xl font-bold ${getIncomeColor(monthlyIncome)}`}>
            {formatCurrency(monthlyIncome)}
          </p>
          <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
            {getIncomeMessage(monthlyIncome)}
          </p>
        </div>

        {showDetails && (
          <>
            {/* Active Sources */}
            {activeSources.length > 0 && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700/30 rounded-lg p-3">
                <div className="flex items-center space-x-2 mb-2">
                  <Plus className="w-4 h-4 text-green-500" weight="duotone" />
                  <span className="text-sm font-medium text-green-700 dark:text-green-400">
                    Active Sources ({activeSources.length})
                  </span>
                </div>
                <div className="text-xs text-gray-700 dark:text-gray-400 space-y-1">
                  {activeSources.map((source) => (
                    <div
                      key={source.id}
                      className="flex items-center justify-between">
                      <div className="flex items-center space-x-1">
                        <span>
                          • {source.name}: {formatCurrency(source.amount)}{" "}
                          {getFrequencyLabel(source.frequency)}
                        </span>
                      </div>
                    </div>
                  ))}
                  {partners &&
                    partners.length > 0 &&
                    partners.map((partner) => (
                      <div
                        key={`partner-${partner.id}`}
                        className="flex items-center justify-between">
                        <div className="flex items-center space-x-1">
                          <span>
                            • {partner.name}'s Income:{" "}
                            {formatCurrency(partner.monthlyIncome)} Monthly
                          </span>
                          <Users
                            className="w-3 h-3 text-blue-500"
                            weight="duotone"
                          />
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Income Composition */}
            <div className="text-xs text-gray-700 dark:text-gray-400 space-y-1">
              <p>• Recurring income: {formatCurrency(totalFromSources)}</p>
              {oneTimeIncome > 0 && (
                <p>• One-time income: {formatCurrency(oneTimeIncome)}</p>
              )}
            </div>
          </>
        )}

        {!showDetails && (
          <div className="text-xs text-gray-700 dark:text-gray-400">
            <p>{activeSources.length} active sources</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default IncomeCard;
